import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { ROUTES } from '@/app/config/constants';
import { LazyLoad } from '@/shared/components/LazyLoad';
import { AuthNoticePage } from './login/pages/AuthNoticePage';

const LoginPage = lazy(() => import('./login/pages/LoginPage'));
const SupplierLoginPage = lazy(() => import('./login/pages/SupplierLoginPage'));
const ForgotPasswordPage = lazy(() => import('./password-recovery/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./password-recovery/pages/ResetPasswordPage'));

/**
 * The feature owns its own route table (niraxobid.md section 23.15 step 10);
 * app/routes/publicRoutes.tsx just composes it.
 *
 * Two decisions are settled here, and both are visible in what this table does:
 *
 * **Password recovery is built.** The platform already serves
 * `/auth/password/forgot` and `/auth/password/reset`, and the sign-in page links
 * to it, so the screens exist rather than apologising.
 *
 * **Registration is not, and will not be.** Accounts are created by an
 * administrator; the design reference offers no route to a sign-up and there is
 * no endpoint behind one. The route stays only so an old bookmark says why
 * rather than 404ing, and it redirects nowhere — it explains and offers sign-in.
 */
export const authRoutes: RouteObject[] = [
  {
    path: ROUTES.LOGIN,
    element: (
      <LazyLoad>
        <LoginPage />
      </LazyLoad>
    ),
  },
  /* The Supplier Bid Quotation portal's sign-in (2026-10-23): the OTP screen first, for a supplier. */
  {
    path: ROUTES.SUPPLIER_LOGIN,
    element: (
      <LazyLoad>
        <SupplierLoginPage />
      </LazyLoad>
    ),
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    element: (
      <LazyLoad>
        <ForgotPasswordPage />
      </LazyLoad>
    ),
  },
  {
    path: ROUTES.RESET_PASSWORD,
    element: (
      <LazyLoad>
        <ResetPasswordPage />
      </LazyLoad>
    ),
  },
  {
    path: ROUTES.REGISTER,
    element: (
      <AuthNoticePage
        title="Registration is not open"
        message="Accounts on this platform are created by an administrator, not by signing up. If you need access, ask your organization's administrator to add you."
      />
    ),
  },
  {
    path: ROUTES.UNAUTHORIZED,
    element: (
      <AuthNoticePage
        title="You do not have access to that page"
        message="Your account is signed in, but it does not carry the permission this page needs. If that looks wrong, ask your administrator to check your role."
        comingSoon={false}
      />
    ),
  },
  /*
    Where `/t/c/{token}` sends a reader whose invitation link has stopped leading anywhere -- the BID's
    deadline has passed, or the token names no link at all. The click is still recorded; what this page
    answers is the person holding a link that no longer works, and the one thing they need to know is that
    the invitation is closed rather than that the site is broken.

    It says nothing about which of the two it was. A token naming no link is either an expired one or a
    guessed one, and telling those apart out loud tells whoever is guessing when they have guessed right.
  */
  {
    path: ROUTES.INVITATION_INVALID,
    element: (
      <AuthNoticePage
        title="This invitation is no longer valid"
        message="The link you followed has expired or is no longer recognised. If the BID is still open, ask the buyer to send the invitation again; if you already have an account, you can sign in to see your invitations."
        comingSoon={false}
      />
    ),
  },
  /* An unknown public path is sign-in, not a dead end. */
  { path: '*', element: <Navigate to={ROUTES.LOGIN} replace /> },
];
